/**
 * Agency/Organization entity DTOs
 * Updated to match actual backend API response structure
 */

import { Timestamps } from '../common/base.dto';

/**
 * Agency contact information as returned by the backend API
 */
export interface AgencyContactInfo {
  officePhone: string;
  fax?: string | null;
  billingEmail?: string;
}

/**
 * Agency address as returned by the backend API
 */
export interface AgencyAddress {
  street: string;
  city: string;
  state: string;
  /** 5-digit ZIP (Phase 5: a legacy "12345-6789" value is split by the backend on read) */
  zipCode: string;
  /** ZIP+4 extension, 4 digits */
  zipPlus4?: string | null;
}

/**
 * US time zones an agency can operate in (Phase 5, D7). Default America/Chicago.
 */
export const AGENCY_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Phoenix', label: 'Arizona (America/Phoenix)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
] as const;

export type AgencyTimezone = typeof AGENCY_TIMEZONES[number]['value'];

export const DEFAULT_AGENCY_TIMEZONE: AgencyTimezone = 'America/Chicago';

/**
 * Agency status types
 */
export type AgencyStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED';

/**
 * Agency entity as received from the backend API
 */
export interface Agency extends Timestamps {
  tenantId: string;
  agencyId: string;
  legalName: string;
  dba?: string | null;
  npi?: string;
  taxId?: string;
  medicaidNumber?: string;
  medicareNumber?: string;
  stateLicenseNumber?: string;
  status: AgencyStatus;
  contactInfo: AgencyContactInfo;
  address: AgencyAddress;
  createdBy?: string;
  updatedBy?: string;
  // EVV configuration stored on the agency
  evvConfig?: EVVConfig;
  evvOnboarding?: EVVOnboarding;
  evvSystemAdmin?: EVVSystemAdmin;
  hhaxProviderId?: string | null;
  /** R-177: collect EVV visit data for PDN (EVV-optional in Texas); default false */
  evvCollectPdnVisits?: boolean;
  /** Phase 20A: disciplines the agency offers (Agency Setup > Services offered); default all */
  servicesOffered?: string[];
  /** Provider taxonomy code: 9 digits followed by a digit or X (Phase 5) */
  taxonomyCode?: string | null;
  /** TX Medicaid benefit code, up to 20 characters (Phase 5) */
  benefitCode?: string | null;
  /** IANA time zone (Phase 5); defaults to America/Chicago */
  timezone?: string | null;
  /** Some backends return zipPlus4 at the top level as well as on address */
  zipPlus4?: string | null;
}

export type EVVOnboardingStepStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

/**
 * One EVV onboarding step as returned by GET /agencies/{id}/evv-onboarding (Phase 5).
 * The step list, labels and resource links come from the API, not the frontend.
 */
export interface EvvOnboardingStepDto {
  key: string;
  label: string;
  description?: string | null;
  status: EVVOnboardingStepStatus;
  /** Official resource link, or null when there is none (no link is shown) */
  resourceUrl: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface EvvOnboardingDto {
  steps: EvvOnboardingStepDto[];
  complete: boolean;
  hhaxProviderId: string | null;
  evvSystemAdmin: { name: string | null; email: string | null };
}

/** Body for PUT /agencies/{id}/evv-onboarding */
export interface EvvOnboardingUpdateRequest {
  stepUpdate?: { key: string; status: EVVOnboardingStepStatus };
  hhaxProviderId?: string;
  evvSystemAdmin?: { name: string | null; email: string | null };
}

/** One field error from a 400 AGENCY_VALIDATION response */
export interface AgencyFieldError {
  field: string;
  message: string;
}

/** @deprecated legacy 5-step ids; the step list now comes from the API */
export type EVVOnboardingStepId =
  | 'FORM_SUBMITTED'
  | 'ADMIN_DESIGNATED'
  | 'LMS_COMPLETE'
  | 'CREDENTIALS_RECEIVED'
  | 'TEST_CLOCKIN';

export interface EVVOnboardingStep {
  id: EVVOnboardingStepId;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';
  completedAt?: string | null;
  owner?: string | null;
  note?: string | null;
  link?: string | null;
}

export interface EVVOnboarding {
  steps: EVVOnboardingStep[];
  complete: boolean;
}

export interface EVVSystemAdmin {
  name?: string | null;
  email?: string | null;
}

/**
 * Flat agency data for create/update requests
 * The service layer transforms this to the nested structure expected by the API
 */
export interface AgencyData {
  legalName: string;
  dba?: string;
  npi?: string;
  taxId?: string;
  medicaidNumber?: string;
  medicareNumber?: string;
  stateLicenseNumber?: string;
  officePhone: string;
  fax?: string;
  billingEmail?: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  zipPlus4?: string;
  taxonomyCode?: string;
  benefitCode?: string;
  timezone?: string;
  tenantId?: string;
  // Optional EVV configuration for onboarding
  evvConfig?: {
    providerId?: string;
    adminEmail?: string;
    onboardingComplete?: boolean;
  };
  evvOnboarding?: EVVOnboarding;
  evvSystemAdmin?: EVVSystemAdmin;
  hhaxProviderId?: string;
  evvCollectPdnVisits?: boolean;
  /** Phase 20A: disciplines the agency offers; at least one */
  servicesOffered?: string[];
}

/**
 * EVV configuration stored on an agency
 */
export interface EVVConfig {
  providerId?: string;
  adminEmail?: string;
  onboardingComplete?: boolean;
}

/**
 * @deprecated Use Agency interface instead - kept for backwards compatibility
 */
export interface LegacyAgency extends Timestamps {
  id: string;
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  faxNumber?: string;
  email?: string;
  website?: string;
  npi?: string;
  taxId?: string;
  medicareId?: string;
  medicaidId?: string;
  tenantId: string;
}
